import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { searchCustomers } from "../api";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function renderContact(email, phone) {
  if (!email && !phone) {
    return "—";
  }
  return (
    <Stack spacing={0.5}>
      {email && (
        <Typography variant="body2" color="text.secondary">
          {email}
        </Typography>
      )}
      {phone && (
        <Typography variant="body2" color="text.secondary">
          {phone}
        </Typography>
      )}
    </Stack>
  );
}

export default function CustomersPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["customers", { query: debouncedSearch, page, size: rowsPerPage }],
    queryFn: () =>
      searchCustomers(token, {
        query: debouncedSearch || undefined,
        page,
        size: rowsPerPage,
      }),
    enabled: Boolean(token),
    keepPreviousData: true,
  });

  const customers = data?.items ?? [];
  const total = data?.totalElements ?? 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showTable = !isLoading && !error && customers.length > 0;
  const showEmpty = !isLoading && !error && customers.length === 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1">
          Kunden
        </Typography>
        <TextField
          label="Suche"
          placeholder="Name, E-Mail oder Telefon"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Paper sx={{ position: "relative" }}>
          {isFetching && !isLoading && (
            <LinearProgress
              sx={{ position: "absolute", top: 0, left: 0, right: 0, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}
            />
          )}
          {isLoading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ py: 6, px: 3 }}>
              <Typography color="error" variant="body1">
                {error.message || "Fehler beim Laden der Kunden."}
              </Typography>
            </Box>
          ) : (
            <>
              {showTable && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Firma</TableCell>
                      <TableCell>Kontakt</TableCell>
                      <TableCell>Einwilligung</TableCell>
                      <TableCell>Letzte Aktivität</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow
                        key={customer.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        <TableCell>{customer.displayName || "—"}</TableCell>
                        <TableCell>{customer.company || "—"}</TableCell>
                        <TableCell>{renderContact(customer.email, customer.phone)}</TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              customer.consentGiven
                                ? `Erteilt: ${formatDateTime(customer.consentTimestamp)}`
                                : "Keine Einwilligung dokumentiert"
                            }
                          >
                            <Chip
                              label={customer.consentGiven ? "ja" : "nein"}
                              color={customer.consentGiven ? "success" : "default"}
                              size="small"
                              variant={customer.consentGiven ? "filled" : "outlined"}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatDateTime(customer.lastActivity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {showEmpty && (
                <Box sx={{ py: 6, px: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    Keine Kunden gefunden.
                  </Typography>
                </Box>
              )}
            </>
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25]}
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Stack>
    </Container>
  );
}
